import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEOHead from '../components/SEOHead';
import PublicSiteShell from '../components/PublicSiteShell';
import BrandLoadingScreen from '../components/BrandLoadingScreen';
import { LANDING_MAP } from '../data/seoLandingPages';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function formatMoney(amount) {
  return `₦${Number(amount || 0).toLocaleString('en-NG')}`;
}

function HouseCard({ house, onOpen }) {
  const image = house.images?.[house.cover_image_index ?? 0] || house.images?.[0] || '/Logo.jpg';
  const isSample = String(house.status || '').toLowerCase() === 'sample';
  const cta = isSample ? 'Join Waitlist' : 'View Home';
  return (
    <article className="rounded-[1.75rem] overflow-hidden border border-[#e8f2d1] bg-white shadow-[0_16px_50px_rgba(41,76,121,0.08)] hover:-translate-y-1 transition">
      <img src={image} alt={house.title || 'LiteRent home'} className="h-56 w-full object-cover" />
      <div className="p-6">
        <div className="flex justify-between gap-3 items-start mb-3">
          <h3 className="text-xl font-semibold text-[#294c79] leading-snug">{house.title || 'Verified Home'}</h3>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${isSample ? 'bg-violet-100 text-violet-700' : 'bg-[#e8f2d1] text-[#294c79]'}`}>{isSample ? 'Sample' : 'Ready'}</span>
        </div>
        <p className="text-sm text-[#1a1a1a]/65 mb-3">{house.location || 'Ibadan'} • {house.bedrooms || 0} bedroom{Number(house.bedrooms) === 1 ? '' : 's'}</p>
        <p className="text-2xl font-bold text-[#294c79] mb-2">{formatMoney(house.annual_rent)} <span className="text-sm font-medium text-[#1a1a1a]/55">/ year</span></p>
        <p className="text-sm text-[#1a1a1a]/65 leading-relaxed min-h-[3.5rem]">{house.description?.slice(0, 120) || 'Browse the details of this LiteRent home and see whether it fits your budget and area preference.'}{house.description?.length > 120 ? '…' : ''}</p>
        <button onClick={() => onOpen(house)} className="mt-5 w-full rounded-2xl bg-[#294c79] hover:bg-[#1f3a5c] text-white font-semibold py-3.5">{cta}</button>
      </div>
    </article>
  );
}

export default function SeoLandingPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, '');
  const config = LANDING_MAP[slug];
  const navigate = useNavigate();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!config) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ page: '1', limit: '12' });
        if (config.location) params.set('location', config.location);
        if (config.minRent) params.set('minRent', config.minRent);
        if (config.maxRent) params.set('maxRent', config.maxRent);
        if (config.bedrooms) params.set('bedrooms', config.bedrooms);
        const res = await axios.get(`${API_URL}/api/houses?${params.toString()}`);
        if (active) setHouses(res.data.houses || []);
      } catch (err) {
        if (active) setError('We could not load listings for this page right now.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [config]);

  const structuredData = useMemo(() => {
    if (!config) return null;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: config.metaTitle,
        url: `https://literent.com.ng/${config.slug}`,
        description: config.description,
        about: 'Rental homes in Ibadan',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://literent.com.ng/' },
          { '@type': 'ListItem', position: 2, name: config.metaTitle, item: `https://literent.com.ng/${config.slug}` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faq.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
    ];
  }, [config]);

  if (!config) {
    return (
      <PublicSiteShell>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl font-bold text-[#294c79] mb-4">Page not found</h1>
          <p className="text-[#1a1a1a]/65 mb-8">This LiteRent page could not be found.</p>
          <Link to="/browse" className="rounded-full bg-[#294c79] text-white px-8 py-4 font-semibold">Browse homes</Link>
        </div>
      </PublicSiteShell>
    );
  }

  const relatedPages = (config.related || []).map((pageSlug) => LANDING_MAP[pageSlug]).filter(Boolean);

  return (
    <PublicSiteShell>
      <SEOHead
        title={config.title}
        description={config.description}
        canonical={`https://literent.com.ng/${config.slug}`}
        structuredData={structuredData}
      />

      <section className="bg-gradient-to-br from-[#294c79] via-[#2f5588] to-[#1f3a5c] text-white px-6 py-16 sm:py-20 overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(165,194,80,0.18),transparent_35%)]" />
        <div className="max-w-7xl mx-auto relative z-10">
          <span className="inline-flex items-center rounded-full bg-white/10 border border-white/15 px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold">{config.heroTag}</span>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mt-5 max-w-4xl">{config.heading}</h1>
          <p className="mt-5 text-lg text-white/78 max-w-3xl leading-relaxed">{config.intro}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/browse" className="rounded-full bg-[#a5c250] text-white px-7 py-3.5 font-semibold hover:bg-[#8bb83a] transition">Browse all homes</Link>
            <Link to="/how-it-works" className="rounded-full border border-white/30 px-7 py-3.5 font-semibold hover:bg-white/10 transition">See how LiteRent works</Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1.4fr_0.8fr] gap-8">
        <div className="rounded-[2rem] bg-white border border-[#edf2f7] p-7 shadow-[0_18px_60px_rgba(41,76,121,0.08)]">
          <h2 className="text-2xl font-bold text-[#294c79] mb-4">Why renters use LiteRent for searches like this</h2>
          <p className="text-[#1a1a1a]/72 leading-8 text-[15px]">LiteRent keeps the rental journey clear and structured. Instead of leaving you to chase scattered listings, we help eligible renters in Ibadan discover verified homes, understand the 30% upfront path, and move from interest to application with more confidence.</p>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {[
              ['Verified listings', 'Homes are shown with clearer details so renters know what they are exploring before they apply.'],
              ['Simpler move-in path', 'LiteRent is built to reduce the one-time upfront pressure for eligible renters.'],
              ['Ibadan-first guidance', 'Pages like this make it easier to compare areas, budgets, and bedroom options without confusion.'],
            ].map(([label, text]) => (
              <div key={label} className="rounded-3xl border border-[#e8f2d1] bg-[#f8fbf2] p-5">
                <h3 className="text-sm uppercase tracking-[0.18em] text-[#294c79]/70 font-semibold mb-3">{label}</h3>
                <p className="text-sm text-[#1a1a1a]/68 leading-6">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] bg-[#fbfcfe] border border-[#edf2f7] p-7 shadow-[0_18px_60px_rgba(41,76,121,0.05)]">
          <h2 className="text-2xl font-bold text-[#294c79] mb-4">Popular next steps</h2>
          <div className="space-y-3">
            <Link to="/browse" className="flex items-center justify-between gap-4 rounded-2xl border border-[#d9e5ef] bg-white px-5 py-4 font-semibold text-[#294c79] hover:border-[#294c79] hover:bg-[#f8fbff] transition"><span>Browse homes</span><span className="rounded-full bg-[#294c79] text-white px-3 py-1 text-xs font-bold uppercase tracking-wide">Open</span></Link>
            <Link to="/waitlist" className="flex items-center justify-between gap-4 rounded-2xl border border-[#d9e5ef] bg-white px-5 py-4 font-semibold text-[#294c79] hover:border-[#294c79] hover:bg-[#f8fbff] transition"><span>Join the waitlist</span><span className="rounded-full bg-[#294c79] text-white px-3 py-1 text-xs font-bold uppercase tracking-wide">Open</span></Link>
            <Link to="/how-it-works" className="flex items-center justify-between gap-4 rounded-2xl border border-[#d9e5ef] bg-white px-5 py-4 font-semibold text-[#294c79] hover:border-[#294c79] hover:bg-[#f8fbff] transition"><span>See how LiteRent works</span><span className="rounded-full bg-[#294c79] text-white px-3 py-1 text-xs font-bold uppercase tracking-wide">Open</span></Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <h2 className="text-3xl font-bold text-[#294c79]">Listings related to this search</h2>
            <p className="text-[#1a1a1a]/65 mt-2">These homes help renters move from search to a clearer LiteRent next step.</p>
          </div>
          <Link to="/browse" className="text-[#294c79] font-semibold hover:underline">View all homes</Link>
        </div>

        {loading ? <BrandLoadingScreen message="Loading relevant homes for this search page…" fullScreen={false} compact /> : null}
        {!loading && error ? <div className="rounded-3xl border border-red-200 bg-red-50 p-5 text-red-700">{error}</div> : null}
        {!loading && !error && houses.length === 0 ? (
          <div className="rounded-[2rem] border border-[#edf2f7] bg-white p-10 text-center">
            <h3 className="text-2xl font-semibold text-[#294c79] mb-3">No homes matched this page yet</h3>
            <p className="text-[#1a1a1a]/65 mb-6">Inventory changes over time. Browse all homes or join the waitlist to hear when more listings are added.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/browse" className="rounded-full bg-[#294c79] text-white px-6 py-3.5 font-semibold">Browse homes</Link>
              <Link to="/waitlist" className="rounded-full border border-[#294c79] text-[#294c79] px-6 py-3.5 font-semibold">Join waitlist</Link>
            </div>
          </div>
        ) : null}

        {!loading && !error && houses.length > 0 ? (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {houses.map((house) => <HouseCard key={house.id} house={house} onOpen={(selected) => navigate(`/house/${selected.id}`)} />)}
          </div>
        ) : null}
      </section>

      <section className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-8">
        <div className="rounded-[2rem] bg-white border border-[#edf2f7] p-8 shadow-[0_18px_60px_rgba(41,76,121,0.08)]">
          <h2 className="text-3xl font-bold text-[#294c79] mb-6">Frequently asked questions</h2>
          <div className="space-y-4">
            {config.faq.map(([question, answer]) => (
              <div key={question} className="rounded-3xl border border-[#edf2f7] bg-[#fbfcfe] p-5">
                <h3 className="text-lg font-semibold text-[#294c79] mb-2">{question}</h3>
                <p className="text-[#1a1a1a]/68 leading-7 text-sm">{answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] bg-[#294c79] text-white p-8 shadow-[0_18px_60px_rgba(41,76,121,0.18)]">
          <h2 className="text-3xl font-bold mb-4">Related LiteRent pages</h2>
          <p className="text-white/75 leading-7 mb-6">Use these pages to keep exploring without getting lost. Each page focuses on a real renter question while keeping LiteRent’s message consistent.</p>
          <div className="space-y-3">
            {relatedPages.map((page) => (
              <Link key={page.slug} to={`/${page.slug}`} className="block rounded-2xl border border-white/15 bg-white/5 px-5 py-4 hover:bg-white/10 transition">
                <div className="font-semibold">{page.metaTitle}</div>
                <div className="text-sm text-white/70 mt-1">{page.description}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </PublicSiteShell>
  );
}
